import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";

// Pages
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Feed from "./pages/Feed";
import Profile from "./pages/Profile";
import Goals from "./pages/Goals";
import Gratitude from "./pages/Gratitude";
import CreatePost from "./pages/CreatePost";
import Events from "./pages/Events";
import Ads from "./pages/Ads";
import Developer from "./pages/Developer";
import Wellness from "./pages/Wellness";
import Achievements from "./pages/Achievements";
import AICamera from "./pages/AICamera";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ChildSafety from "./pages/ChildSafety";
import Planner from "./pages/Planner";
import Friends from "./pages/Friends";
import Messages from "./pages/Messages";
import Joy from "./pages/Joy";
import Analytics from "./pages/Analytics";
import Explore from "./pages/Explore";
import Notifications from "./pages/Notifications";
import Reels from "./pages/Reels";
import Saved from "./pages/Saved";

// Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("happybook_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("happybook_token");
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("happybook_token");
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("happybook_token", response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, bio) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      bio,
    });
    localStorage.setItem("happybook_token", response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("happybook_token");
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser((prev) => ({ ...prev, ...updates }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary/20"></div>
          <div className="h-4 w-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, register, logout, updateUser }}>
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={user ? <Navigate to="/feed" /> : <Landing />}
          />
          <Route
            path="/auth"
            element={user ? <Navigate to="/feed" /> : <Auth />}
          />
          <Route
            path="/feed"
            element={user ? <Feed /> : <Navigate to="/" />}
          />
          <Route
            path="/profile"
            element={user ? <Profile /> : <Navigate to="/" />}
          />
          <Route
            path="/profile/:userId"
            element={user ? <Profile /> : <Navigate to="/" />}
          />
          <Route
            path="/goals"
            element={user ? <Goals /> : <Navigate to="/" />}
          />
          <Route
            path="/gratitude"
            element={user ? <Gratitude /> : <Navigate to="/" />}
          />
          <Route
            path="/create"
            element={user ? <CreatePost /> : <Navigate to="/" />}
          />
          <Route
            path="/events"
            element={user ? <Events /> : <Navigate to="/" />}
          />
          <Route
            path="/ads"
            element={user ? <Ads /> : <Navigate to="/" />}
          />
          <Route
            path="/ads/success"
            element={user ? <Ads /> : <Navigate to="/" />}
          />
          <Route
            path="/developer"
            element={user ? <Developer /> : <Navigate to="/" />}
          />
          <Route
            path="/wellness"
            element={user ? <Wellness /> : <Navigate to="/" />}
          />
          <Route
            path="/achievements"
            element={user ? <Achievements /> : <Navigate to="/" />}
          />
          <Route
            path="/camera"
            element={user ? <AICamera /> : <Navigate to="/" />}
          />
          <Route
            path="/planner"
            element={user ? <Planner /> : <Navigate to="/" />}
          />
          <Route
            path="/friends"
            element={user ? <Friends /> : <Navigate to="/" />}
          />
          <Route
            path="/messages"
            element={user ? <Messages /> : <Navigate to="/" />}
          />
          <Route
            path="/messages/:recipientId"
            element={user ? <Messages /> : <Navigate to="/" />}
          />
          <Route
            path="/joy"
            element={user ? <Joy /> : <Navigate to="/" />}
          />
          <Route
            path="/analytics"
            element={user ? <Analytics /> : <Navigate to="/" />}
          />
          <Route
            path="/explore"
            element={user ? <Explore /> : <Navigate to="/" />}
          />
          <Route
            path="/explore/:category"
            element={user ? <Explore /> : <Navigate to="/" />}
          />
          <Route
            path="/notifications"
            element={user ? <Notifications /> : <Navigate to="/" />}
          />
          <Route
            path="/reels"
            element={user ? <Reels /> : <Navigate to="/" />}
          />
          <Route
            path="/saved"
            element={user ? <Saved /> : <Navigate to="/" />}
          />
          <Route
            path="/privacy-policy"
            element={<PrivacyPolicy />}
          />
          <Route
            path="/child-safety"
            element={<ChildSafety />}
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthContext.Provider>
  );
}

export default App;
